import { Box, Typography, Paper, Chip } from '@mui/material';
import { useMemo } from 'react';
import JsonView from '@uiw/react-json-view';

import NoDataFallback from '../common/NoDataFallback';

interface ServerLogsDataProps {
  parsedLogs: any[];
  searchTerm: string;
}

const ServerLogsData = ({ parsedLogs, searchTerm }: ServerLogsDataProps) => {
  // Enhanced search functionality with regex support and case sensitivity
  const filteredLogs = useMemo(() => {
    if (!searchTerm.trim()) {
      return parsedLogs;
    }

    try {
      // Always use regex with case sensitivity enabled
      const searchPattern = new RegExp(searchTerm, 'g');

      return parsedLogs.filter(log => {
        const logString = JSON.stringify(log, null, 2);
        return searchPattern.test(logString);
      });
    } catch {
      // If regex is invalid, fall back to simple string search with case sensitivity
      return parsedLogs.filter(log => {
        const logString = JSON.stringify(log, null, 2);
        return logString.includes(searchTerm);
      });
    }
  }, [parsedLogs, searchTerm]);

  const formatTimestamp = (timestamp: string | number) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const processLogData = (log: any) => {
    // Deep clone the log to avoid mutating the original
    const processedLog = JSON.parse(JSON.stringify(log));

    // Recursively process string values to convert \n to actual line breaks
    const processValue = (obj: any): any => {
      if (typeof obj === 'string') {
        return obj.replace(/\\n/g, '\n');
      } else if (Array.isArray(obj)) {
        return obj.map(processValue);
      } else if (obj && typeof obj === 'object') {
        const processed: Record<string, any> = {};
        for (const [key, value] of Object.entries(obj)) {
          processed[key] = processValue(value);
        }
        return processed;
      }
      return obj;
    };

    return processValue(processedLog);
  };

  // No Search Results
  if (searchTerm && filteredLogs.length === 0) {
    return (
      <NoDataFallback
        message="No Matching Logs Found"
        description={`No logs match your search term "${searchTerm}". Try adjusting your search criteria.`}
      />
    );
  }

  // Empty State
  if (filteredLogs.length === 0) {
    return (
      <NoDataFallback
        message="No Log Entries Found"
        description="The log file appears to be empty or contains invalid JSON."
      />
    );
  }

  return (
    <Box>
      {/* Search Results Count */}
      {searchTerm && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`${filteredLogs.length} of ${parsedLogs.length} results`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      {/* Logs Display */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filteredLogs.map((log, index) => {
          const processedLog = processLogData(log);

          return (
            <Paper
              key={index}
              elevation={1}
              sx={{
                p: 3,
                borderRadius: '12px',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                backgroundColor: searchTerm ? '#f5f5f5' : '#ffffff',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  borderColor: 'rgba(25, 118, 210, 0.2)',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {/* Header with timestamp and index */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Entry #{index + 1}
                  </Typography>
                  {processedLog.timestamp && (
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(processedLog.timestamp)}
                    </Typography>
                  )}
                </Box>

                {/* Enhanced JSON View */}
                <Box
                  sx={{
                    mt: 1,
                    '& .w-rjv': {
                      fontSize: `14px !important`,
                      fontFamily:
                        "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",
                      lineHeight: 1.5,
                    },
                    '& .w-rjv-container': {
                      backgroundColor: 'transparent !important',
                      border: 'none !important',
                    },
                    '& .w-rjv-row': {
                      padding: '4px 0',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .w-rjv-search-highlight': {
                      backgroundColor: '#ff5722 !important',
                      color: '#fff !important',
                      fontWeight: 'bold !important',
                      padding: '2px 4px',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(255, 87, 34, 0.3)',
                      animation: 'pulse 1.5s ease-in-out infinite alternate',
                    },
                    '& .w-rjv-string': {
                      color: '#2e7d32 !important',
                      wordBreak: 'break-word',
                    },
                    '& .w-rjv-number': {
                      color: '#1976d2 !important',
                    },
                    '& .w-rjv-boolean': {
                      color: '#7b1fa2 !important',
                    },
                    '& .w-rjv-null': {
                      color: '#757575 !important',
                    },
                    '& .w-rjv-key': {
                      color: '#c62828 !important',
                      fontWeight: '500 !important',
                    },
                    '@keyframes pulse': {
                      from: { opacity: 0.8 },
                      to: { opacity: 1 },
                    },
                  }}
                >
                  <JsonView
                    value={processedLog}
                    style={{
                      fontSize: '14px',
                      backgroundColor: 'transparent',
                      fontFamily:
                        "'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace",
                    }}
                    displayDataTypes={false}
                    displayObjectSize={true}
                    collapsed={2}
                    enableClipboard={true}
                    indentWidth={20}
                    shortenTextAfterLength={searchTerm ? undefined : 100}
                  />
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default ServerLogsData;
